/**
 * Mes élèves et la fiche d'un élève, côté tuteur hors live : du store aux props des pages.
 *
 * - `tutorStudentsViewModel` (patron A) : la liste vit dans `currentUser.object.students`, remplie par
 *   `getStudents` (que `MyStudentsTabScreen` relit à chaque focus) ; le chargement se lit sur la tranche
 *   ET l'absence de lignes, comme l'ancienne roue (`getStudentRequest && !students.length`).
 * - `makeTutorStudentProfileViewModel` (patron A, et B en repli) : l'élève relu par `getStudent`, que le
 *   réducteur recopie dans la liste du tuteur — sinon la réponse elle-même, gardée par l'écran (un élève
 *   ouvert depuis l'historique des lives n'est pas toujours dans la liste, et le réducteur ne l'y ajoute
 *   pas) ; le brief n'atteint jamais le store : il arrive en argument. Fabrique : la fiche peut être
 *   montée deux fois à la fois (au large, à droite de la liste, et poussée par-dessus).
 */
import { createSelector } from "reselect"

import { subjectPriorities } from "../../../../../data/constants/formConstants"
import { indexSubjectsById } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    type ApiTutorOwnSession,
    type ApiTutorStudent,
    type ApiTutorStudentBrief,
    type TutorStudentContext,
    toUITutorStudent,
    toUITutorStudentProfile,
} from "../../../adapters/tutor/Students/tutorStudentAdapter"
import { type UITutorStudent, type UITutorStudentProfile } from "../../../ui/types/tutorEleves"
import { selectAccount, selectAccountStudents } from "../../shared/displayedStudent"

type State = any // eslint-disable-line @typescript-eslint/no-explicit-any
type Slice = { request?: boolean; error?: unknown } | undefined

/** Le relecteur et l'admin lisent les coordonnées (`ProfilDetails` : `backOffice` ou `review_activities`). */
const CONTACT_ROLES = ["super-admin", "admin", "reviewer"]

const schoolSubjectsOf = (state: State) => state?.data?.staticData?.school_subjects
const specialNeedsOf = (state: State) => state?.data?.staticData?.student_special_needs
const personalWorkFormOf = (state: State) => state?.data?.staticData?.personal_work_form
const tutorSessionsOf = (state: State) => selectAccount(state)?.live_sessions as ApiTutorOwnSession[] | undefined
const rolesOf = (state: State) => selectAccount(state)?.roles as string[] | undefined

/** Ce que l'adaptateur lit à côté de l'élève. */
export const tutorStudentContext = createSelector(
    [schoolSubjectsOf, specialNeedsOf, personalWorkFormOf, tutorSessionsOf, rolesOf],
    (subjects, specialNeeds, personalWorkForm, sessions, roles): TutorStudentContext => ({
        subjectsById: indexSubjectsById(subjects),
        specialNeedLabels: specialNeeds ?? null,
        personalWorkForm: personalWorkForm ?? null,
        subjectPriorities,
        tutorSessions: sessions ?? null,
        canSeeContacts: (roles ?? []).some((role) => CONTACT_ROLES.includes(role)),
    })
)

export type TutorStudentsViewModel = {
    students: UITutorStudent[]
    /** Le premier chargement : rien à montrer encore. */
    loading: boolean
    /** Une relecture, la liste déjà là : la flèche tourne. */
    refreshing: boolean
    /** Une lecture en échec sans rien à montrer. L'ancien écran restait vide : la panne se dit. */
    error: unknown
}

const studentsSliceOf = (state: State) => state?.api?.getStudents as Slice

export const tutorStudentsViewModel = createSelector(
    [selectAccountStudents, tutorStudentContext, studentsSliceOf],
    (rows, context, slice): TutorStudentsViewModel => {
        const students = (rows as ApiTutorStudent[])
            .map((row) => toUITutorStudent(row, context))
            .filter((student): student is UITutorStudent => student != null)
        const empty = students.length === 0
        // Jamais lue dans cette session (la tranche n'existe pas encore) : l'attente, pas « Aucun élève »
        // le temps d'une image avant la première lecture.
        const neverRead = slice == null
        return {
            students,
            loading: empty && (neverRead || slice?.request === true),
            refreshing: !empty && slice?.request === true,
            error: empty && slice?.request !== true ? (slice?.error ?? null) : null,
        }
    }
)

/**
 * « À traiter » d'abord (la grammaire du live) : les élèves dont des messages attendent, puis les autres, chacun
 * dans l'ordre du serveur. `held` garde en tête ceux dont la conversation vient d'être lue, jusqu'à la relecture de
 * la liste : une ligne ne quitte pas sa place sous les yeux du masteur au moment où il la lit.
 */
export function unreadFirst(students: UITutorStudent[], held: ReadonlySet<string>): UITutorStudent[] {
    const waiting = (student: UITutorStudent) => (student.unreadMessagesCount ?? 0) > 0 || held.has(String(student.id))
    const first = students.filter(waiting)
    if (first.length === 0 || first.length === students.length) return students
    return [...first, ...students.filter((student) => !waiting(student))]
}

export type TutorStudentProfileInputs = {
    studentId: string | number | null
    /** La réponse de `getStudent`, gardée par l'écran : le repli d'un élève hors de la liste. */
    raw: ApiTutorStudent | null
    brief: ApiTutorStudentBrief | null
}

/** Une fiche par lecteur : `const vm = useMemo(makeTutorStudentProfileViewModel, [])`. */
export function makeTutorStudentProfileViewModel() {
    const studentOf = (state: State, inputs: TutorStudentProfileInputs): ApiTutorStudent | null => {
        if (inputs.studentId == null) return null
        const listed = (selectAccountStudents(state) as ApiTutorStudent[]).find(
            (student) => String(student?.id) === String(inputs.studentId)
        )
        if (listed != null) return listed
        return inputs.raw != null && String(inputs.raw.id) === String(inputs.studentId) ? inputs.raw : null
    }
    return createSelector(
        [studentOf, (_state: State, inputs: TutorStudentProfileInputs) => inputs.brief, tutorStudentContext],
        (student, brief, context): UITutorStudentProfile | null => toUITutorStudentProfile(student, brief, context)
    )
}
